
import React from "react";
import PostMetadata from "@/components/admin/PostMetadata";
import PostEditorContent from "@/components/admin/PostEditorContent";
import PostEditorActions from "@/components/admin/PostEditorActions";
import PostEditorHeader from "@/components/admin/PostEditorHeader";
import DeletePostDialog from "@/components/admin/DeletePostDialog";

interface PostEditorProps {
  id?: string;
  title: string;
  setTitle: (title: string) => void;
  slug: string;
  setSlug: (slug: string) => void;
  excerpt: string;
  setExcerpt: (excerpt: string) => void;
  author: string;
  setAuthor: (author: string) => void;
  seoDescription: string;
  setSeoDescription: (desc: string) => void;
  theme: string;
  setTheme: (theme: string) => void;
  categories: string[];
  setCategories: (categories: string[]) => void;
  content: string;
  setContent: (content: string) => void;
  featuredImage: string;
  setFeaturedImage: (url: string) => void;
  featuredImageAlt: string;
  setFeaturedImageAlt: (alt: string) => void;
  featuredImageTitle: string;
  setFeaturedImageTitle: (title: string) => void;
  isDraft: boolean;
  isPending: boolean;
  showDeleteDialog: boolean;
  setShowDeleteDialog: (show: boolean) => void;
  togglePublishStatus: () => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleDeletePost: () => Promise<void>;
  onFileUpload?: (file: File) => Promise<string>;
}

const PostEditor: React.FC<PostEditorProps> = ({
  id,
  title,
  setTitle,
  slug,
  setSlug,
  excerpt,
  setExcerpt,
  author,
  setAuthor,
  seoDescription,
  setSeoDescription,
  theme,
  setTheme,
  categories,
  setCategories,
  content,
  setContent,
  featuredImage,
  setFeaturedImage,
  featuredImageAlt,
  setFeaturedImageAlt,
  featuredImageTitle,
  setFeaturedImageTitle,
  isDraft,
  isPending,
  showDeleteDialog,
  setShowDeleteDialog,
  togglePublishStatus,
  handleSubmit,
  handleDeletePost,
  onFileUpload,
}) => {
  
  const handleViewArticle = () => {
    if (slug) {
      window.open(`/blog/${slug}`, '_blank');
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 md:px-6 lg:px-8">
      <PostEditorHeader id={id} backLink="/admin/blog" />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Post Metadata (Details) edge-to-edge at top */}
        <PostMetadata
          title={title}
          setTitle={setTitle}
          slug={slug}
          setSlug={setSlug}
          excerpt={excerpt}
          setExcerpt={setExcerpt}
          author={author}
          setAuthor={setAuthor}
          seoDescription={seoDescription}
          setSeoDescription={setSeoDescription}
          theme={theme}
          setTheme={setTheme}
          categories={categories}
          setCategories={setCategories}
          featuredImage={featuredImage}
          setFeaturedImage={setFeaturedImage}
          featuredImageAlt={featuredImageAlt}
          setFeaturedImageAlt={setFeaturedImageAlt}
          featuredImageTitle={featuredImageTitle}
          setFeaturedImageTitle={setFeaturedImageTitle}
          onFileUpload={onFileUpload}
        />

        {/* Content Editor below the details */}
        <PostEditorContent
          content={content}
          setContent={setContent}
          theme={theme}
          featuredImage={featuredImage}
          featuredImageAlt={featuredImageAlt}
          featuredImageTitle={featuredImageTitle}
          title={title}
          postId={id}
        />

        {/* Action Footer */}
        <PostEditorActions
          id={id}
          slug={slug}
          isDraft={isDraft}
          isPending={isPending}
          togglePublishStatus={togglePublishStatus}
          setShowDeleteDialog={setShowDeleteDialog}
          handleViewArticle={handleViewArticle}
        />
      </form>

      <DeletePostDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onDelete={handleDeletePost}
        isPending={isPending}
      />
    </div>
  );
};

export default PostEditor;
